import { Color, Style } from "./button-variants";
import { FontStyles } from "../../utils/font-styles.ts";

// base style applied to all buttons
const buttonBaseStyle =
  "text-base font-semibold rounded justify-center items-center gap-2 flex h-10 focus:shadow-[0_0_0_4px] focus:outline-none disabled:select-none";
const solidBasePadding = "px-3.5 py-2.5";
const solidBaseStyle = "active:shadow-[0_0_8px_0_inset] disabled:active:shadow-none disabled:pointer-events-none";

// base style applied to solid buttons
const colorVariants: Record<Color, string> = {
  primary:
    "text-neutral-white bg-north-sea hover:bg-north-sea-800 focus:bg-north-sea focus:shadow-north-sea-100 active:!shadow-north-sea-900 disabled:hover:bg-north-sea",
  secondary:
    "text-neutral-white bg-loch hover:bg-loch-800 focus:bg-loch focus:shadow-loch-100 active:!shadow-loch-800 disabled:hover:bg-loch",
  tertiary:
    "text-neutral-white bg-pond hover:bg-pond-800 focus:bg-pond focus:shadow-pond-50 active:!shadow-pond-800 disabled:hover:bg-pond",
  success:
    "text-neutral-white bg-glen hover:bg-glen-800 focus:bg-glen focus:shadow-glen-50 active:!shadow-glen-800 disabled:hover:bg-glen",
  caution:
    "text-neutral-white bg-autumn hover:bg-autumn-800 focus:bg-autumn focus:shadow-autumn-50 active:!shadow-autumn-800 disabled:hover:bg-autumn",
  alert:
    "text-neutral-white bg-brick hover:bg-brick-800 focus:bg-brick focus:shadow-brick-50 active:!shadow-brick-800 disabled:hover:bg-brick",
  accent:
    "text-slate bg-foggy-firth hover:bg-neutral-200 focus:bg-foggy-firth focus:shadow-neutral-50 active:!shadow-neutral-300 disabled:hover:bg-foggy-firth",
  light:
    "text-slate border-solid border-2 border-neutral-200 bg-cloud hover:bg-neutral-100 focus:bg-cloud focus:shadow-neutral-100 active:!shadow-neutral-200 disabled:hover:bg-cloud",
};

// base style applied to outline buttons
const outlineBaseStyle =
  "bg-neutral-white focus:bg-neutral-white border-2 hover:text-neutral-white active:!shadow-[0_0_6px_0_inset] disabled:pointer-events-none";
const outlineBasePadding = "px-3 py-2";
const outlineVariants: Record<Color, string> = {
  primary: "text-north-sea border-north-sea hover:bg-north-sea focus:shadow-north-sea-100 focus:text-north-sea",
  secondary: "text-loch border-loch hover:bg-loch focus:shadow-loch-100 focus:text-loch",
  tertiary: "text-pond border-pond hover:bg-pond focus:shadow-pond-50 focus:text-pond",
  success: "text-glen border-glen hover:bg-glen focus:shadow-glen-50 focus:text-glen",
  caution: "text-autumn border-autumn hover:bg-autumn focus:shadow-autumn-50 focus:text-autumn",
  alert: "text-brick border-brick hover:bg-brick focus:shadow-brick-50 focus:text-brick",
  accent:
    "text-slate border-foggy-firth hover:bg-foggy-firth hover:text-slate focus:shadow-neutral-50 active:!shadow-foggy-firth",
  light: "", // this variant not present
};

export const getButtonStyles = (style: Style, color: Color, isDisabled?: boolean, isLoading?: boolean): string => {
  const styles: string[] = [buttonBaseStyle];
  switch (style) {
    case "solid":
      styles.push(solidBaseStyle);
      // 'light' needs special treatment as it has an outline, although it's a solid variant
      if (color !== "light") {
        styles.push(solidBasePadding);
      } else {
        styles.push(outlineBasePadding);
      }
      styles.push(colorVariants[color]);
      break;
    case "outlined":
      styles.push(outlineBaseStyle);
      styles.push(outlineBasePadding);
      styles.push(outlineVariants[color]);
  }
  // these styles cover smaller variants with breakpoints, too
  styles.push(FontStyles["body-md"]);
  if (isDisabled || isLoading) {
    styles.push("opacity-30");
  }
  return styles.join(" ");
};
